import { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { RevealLines } from './Reveal'
import { useLocale } from '../i18n/locale.jsx'

const EASE = [0.22, 1, 0.36, 1] // ease-out-quint

// „Custom business apps" — clonă 1:1 a cardului-slider din gaspar.framer.website/about:
//   • split Left (text) / Right (vizual), fundal întunecat + overlay 4%
//   • quote MARE sus · departament + dots la bază · fără săgeți (ca în template)
//   • DREAPTA (în locul fotografiei): NUMELE aplicației uriaș în Fraunces, schimbat per
//     slide cu crossfade + zoom — reprezintă fiecare aplicație, tipografic, $0
//   • dots rotunde (activ 1 / inactiv 0.3) · autoplay în viewport + pauză la hover · swipe
//   • cele 6 carduri se SUPRAPUN PERFECT la scroll (sticky, același top, fără offset/scale)

const HEADER = {
  en: { eyebrow: 'Custom business apps', line1: 'Spreadsheets, manual work and scattered tools,', line2: 'turned into custom apps you own.', sub: 'Finance dashboards, operations boards, client portals and workflow apps — built around the systems you already run, shipped in weeks.' },
  ro: { eyebrow: 'Aplicații de business custom', line1: 'Fișiere Excel, muncă manuală și unelte împrăștiate,', line2: 'transformate în aplicații custom ale tale.', sub: 'Dashboard-uri de finanțe, tablouri operaționale, portaluri pentru clienți și aplicații de workflow — construite în jurul sistemelor pe care le folosești deja, livrate în săptămâni.' },
  de: { eyebrow: 'Massgeschneiderte Business-Apps', line1: 'Tabellen, manuelle Arbeit und verstreute Tools,', line2: 'verwandelt in massgeschneiderte Apps, die Ihnen gehören.', sub: 'Finanz-Dashboards, Betriebs-Boards, Kundenportale und Workflow-Apps — gebaut rund um die Systeme, die Sie schon nutzen, in Wochen geliefert.' },
}

// `visual` = gradientul posterului din dreapta; `nameColor` = culoarea numelui uriaș pe el.
const CARDS = [
  {
    slug: 'finance', num: '01',
    visual: 'radial-gradient(120% 130% at 78% 16%, rgba(232,163,61,0.42), rgba(232,163,61,0.08) 54%, rgba(232,163,61,0) 74%), linear-gradient(160deg, #243029 0%, #18201B 100%)',
    nameColor: 'text-cream/85',
    apps: ['Month-End Close Cockpit', 'Cash-Flow Dashboard', 'Collections Autopilot', 'Consolidation Hub'],
  },
  {
    slug: 'operations', num: '02',
    visual: 'linear-gradient(150deg, #F4C988 0%, #E8A33D 55%, #C68A30 100%)',
    nameColor: 'text-forest/80',
    apps: ['Ops Dashboard', 'Excel → App', 'Workflow Automation', 'Stack Sync'],
  },
  {
    slug: 'sales', num: '03',
    visual: 'linear-gradient(150deg, #F4EFE5 0%, #ECE6D8 55%, #DDD2BC 100%)',
    nameColor: 'text-forest/75',
    apps: ['Quote Engine', 'Client Portal', 'Lead Router', 'Pipeline Dashboard'],
  },
  {
    slug: 'people', num: '04',
    visual: 'linear-gradient(160deg, #B5BFB8 0%, #9FAFA4 55%, #8FA396 100%)',
    nameColor: 'text-forest/80',
    apps: ['Onboarding Flow', 'Approval Workflows', 'Employee Portal', 'HR Records'],
  },
  {
    slug: 'procurement', num: '05',
    visual: 'linear-gradient(150deg, #EFE6CF 0%, #E6D6B4 55%, #D3C094 100%)',
    nameColor: 'text-forest/75',
    apps: ['PO Workflow', 'Spend Dashboard', 'Vendor Hub', 'Contract Tracker'],
  },
]

const LABELS = {
  finance: { en: 'Finance Apps', ro: 'Aplicații finanțe', de: 'Finanz-Apps' },
  operations: { en: 'Operations Apps', ro: 'Aplicații operațiuni', de: 'Betriebs-Apps' },
  sales: { en: 'Sales Portals', ro: 'Portaluri vânzări', de: 'Vertriebs-Portale' },
  people: { en: 'People Flows', ro: 'Fluxuri HR', de: 'Personal-Flows' },
  procurement: { en: 'Procurement Tools', ro: 'Unelte achiziții', de: 'Einkaufs-Tools' },
}

const DESCS = {
  finance: {
    en: ['Every entity’s close, checklist and sign-off in one board — wired to QuickBooks or NetSuite.', 'See your real cash position and 13-week runway, live.', 'Chase overdue invoices automatically and get paid sooner.', 'One consolidated P&L across all your entities — no Excel roll-up.'],
    ro: ['Închiderea fiecărei entități, checklist și aprobări pe un singur board.', 'Vezi poziția reală de cash și runway-ul, live.', 'Urmărește automat facturile restante și încasează mai repede.', 'Un P&L consolidat pentru toate entitățile tale.'],
    de: ['Der Abschluss jeder Einheit, Checkliste und Freigabe auf einem Board — angebunden an bexio oder Abacus.', 'Ihre echte Liquidität und 13-Wochen-Runway, live — aus PostFinance, UBS und Ihrem ERP.', 'Überfällige QR-Rechnungen automatisch nachfassen, schneller bezahlt.', 'Eine konsolidierte Erfolgsrechnung über alle Einheiten — ohne Excel-Konsolidierung.'],
  },
  operations: {
    en: ['Orders, tickets, stock and KPIs — live in one place.', 'Turn your most important spreadsheet into a real app.', 'Kill the repetitive manual steps across your tools.', 'Connect NetSuite, Shopify and your WMS — data stops being re-typed.'],
    ro: ['Comenzi, tichete, stoc și KPI-uri — live, într-un singur loc.', 'Transformă cel mai important Excel într-o aplicație reală.', 'Elimină pașii manuali repetitivi dintre uneltele tale.', 'Conectează-ți sistemele ca să nu mai reintroduci date.'],
    de: ['Bestellungen, Tickets, Lager und KPIs — live an einem Ort.', 'Ihre wichtigste Tabelle wird zur echten App.', 'Schluss mit wiederholten manuellen Schritten über Ihre Tools.', 'Insellösungen verbinden — bexio, KLARA, das CRM. Keine Doppelerfassung.'],
  },
  sales: {
    en: ['Build and send branded, priced quotes in minutes.', 'A private space where clients order, approve and track.', 'Capture every lead from Salesforce, HubSpot or a form — routed in seconds.', 'See real pipeline health across all your tools.'],
    ro: ['Creează și trimite oferte brănduite și prețuite în minute.', 'Un spațiu privat unde clienții comandă, aprobă și urmăresc.', 'Capturează fiecare lead și rutează-l în câteva secunde.', 'Vezi sănătatea reală a pipeline-ului din toate uneltele.'],
    de: ['Gebrandete, kalkulierte Offerten in Minuten erstellen und senden.', 'Ein privater Raum, in dem Kunden bestellen, freigeben und verfolgen.', 'Jeden Lead erfassen und in Sekunden zuweisen.', 'Echte Pipeline-Gesundheit über alle Tools sehen.'],
  },
  people: {
    en: ['Every new-hire step, tracked from offer to day one.', 'PTO and requests approved, never lost in inboxes.', 'Self-service for pay slips, time off and documents.', 'Compliant employee files with automatic expiry alerts.'],
    ro: ['Fiecare pas de onboarding, de la ofertă la prima zi.', 'Concedii și cereri aprobate, niciodată pierdute în inbox.', 'Self-service pentru fluturași, concedii și documente.', 'Dosare conforme, cu alerte automate de expirare.'],
    de: ['Jeder Onboarding-Schritt, vom Angebot bis zum ersten Tag.', 'Ferien und Anträge freigegeben, nie im Postfach verloren.', 'Self-Service für Lohnabrechnungen, Abwesenheiten und Dokumente.', 'Konforme Personaldossiers mit automatischen Ablauf-Erinnerungen.'],
  },
  procurement: {
    en: ['Request, approve and log every PO against budget.', "See every dollar committed before it's spent.", 'Suppliers, documents and risk flags in one place.', 'Never miss a renewal, approval or obligation again.'],
    ro: ['Solicită, aprobă și înregistrează fiecare comandă pe buget.', 'Vezi fiecare leu angajat înainte să fie cheltuit.', 'Furnizori, documente și semnale de risc într-un singur loc.', 'Nicio reînnoire, aprobare sau obligație ratată.'],
    de: ['Jede Bestellung anfragen, freigeben und aufs Budget buchen.', 'Jeden zugesagten Franken sehen, bevor er ausgegeben wird.', 'Lieferanten, Dokumente und Risiko-Signale an einem Ort — auch DE/FR/IT.', 'Keine Verlängerung, Freigabe oder Pflicht mehr verpassen.'],
  },
}

const ROI = {
  en: { title: 'What you actually get.', points: ['Shipped in weeks, not quarters.', 'Owned by you — wired into the tools you already run.', 'Replaces the spreadsheet, not the system you trust.'], tag: 'The payoff', poster: 'Yours.' },
  ro: { title: 'Ce primești, de fapt.', points: ['Livrat în săptămâni, nu trimestre.', 'Al tău — conectat la uneltele pe care le folosești deja.', 'Înlocuiește fișierul Excel, nu sistemul în care ai încredere.'], tag: 'Ce câștigi', poster: 'Al tău.' },
  de: { title: 'Was Sie wirklich bekommen.', points: ['In Wochen geliefert, nicht in Quartalen.', 'Ihnen gehörend — verbunden mit den Tools, die Sie schon nutzen.', 'Ersetzt die Tabelle, nicht das System, dem Sie vertrauen.'], tag: 'Was Sie gewinnen', poster: 'Ihres.' },
}

const PANEL_BG = 'linear-gradient(0deg, rgba(255,255,255,0.04), rgba(255,255,255,0.04)), #141916'

function useReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const on = (e) => setReduced(e.matches)
    mq.addEventListener?.('change', on)
    return () => mq.removeEventListener?.('change', on)
  }, [])
  return reduced
}

function useInView(ref, threshold = 0.4) {
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [ref, threshold])
  return inView
}

// Aplicațiile care au deja imagine generată în public/img/apps/ (`${slug}-${n}.png`).
// Pe măsură ce adaugi imagini, adăugăm cheia aici → posterul folosește imaginea în loc de nume.
const HAS_IMG = new Set([
  'finance-1', 'finance-2', 'finance-3', 'finance-4',
  'operations-1', 'operations-2', 'operations-3', 'operations-4',
  'sales-1', 'sales-2', 'sales-3', 'sales-4',
  'people-1', 'people-2', 'people-3', 'people-4',
  'procurement-1', 'procurement-2', 'procurement-3', 'procurement-4',
])

// Posterul din dreapta — în locul fotografiei din template:
//  • dacă există imagine → imaginea (object-cover) + numele aplicației ca etichetă peste ea
//  • dacă nu → NUMELE aplicației uriaș în Fraunces (fallback)
// Ambele se schimbă per slide cu crossfade + zoom.
function Poster({ name, nameColor, visual, slideKey, reduced, imgSrc }) {
  const [failed, setFailed] = useState(() => new Set())
  const showImg = imgSrc && !failed.has(imgSrc)
  return (
    <div aria-hidden className="relative hidden overflow-hidden md:block">
      <div className="absolute inset-0" style={{ background: visual }} />
      <motion.div
        key={slideKey}
        initial={{ opacity: 0, scale: reduced ? 1 : 1.08 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: reduced ? 0 : 0.7, ease: EASE }}
        className="absolute inset-0"
      >
        {showImg ? (
          <>
            <img src={imgSrc} alt="" loading="lazy" onError={() => setFailed((s) => new Set(s).add(imgSrc))} className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(20,25,22,0.7), rgba(20,25,22,0) 48%)' }} />
            <span className="display absolute inset-x-8 bottom-8 leading-[0.98] text-cream md:inset-x-10 lg:inset-x-12 lg:bottom-12" style={{ fontSize: 'clamp(26px, 3.2vw, 44px)' }}>{name}</span>
          </>
        ) : (
          <div className="flex h-full items-center p-12 lg:p-16">
            <span className={`display leading-[0.96] tracking-[-0.01em] ${nameColor}`} style={{ fontSize: 'clamp(42px, 5.4vw, 80px)' }}>{name}</span>
          </div>
        )}
      </motion.div>
    </div>
  )
}

// Toate cardurile la ACELAȘI top → se suprapun perfect (fără offset/scale).
function StickyShell({ cardRef, children }) {
  return (
    <div ref={cardRef} className="sticky top-[112px]">
      <article className="relative mx-auto h-[60vh] max-h-[700px] min-h-[460px] max-w-site overflow-hidden rounded-lg border border-forest-line shadow-[0_-24px_60px_-30px_rgba(0,0,0,0.6)] md:h-[78vh]">
        <div className="grid h-full md:grid-cols-2">{children}</div>
      </article>
    </div>
  )
}

function DeptCard({ card, label, descs }) {
  const ref = useRef(null)
  const inView = useInView(ref, 0.4)
  const reduced = useReducedMotion()
  const apps = card.apps
  const n = apps.length
  const [idx, setIdx] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (reduced || paused || !inView) return
    const t = setInterval(() => setIdx((p) => (p + 1) % n), 4500)
    return () => clearInterval(t)
  }, [reduced, paused, inView, n])

  const go = (d) => setIdx((p) => (p + d + n) % n)
  const onDragEnd = (_e, info) => {
    if (info.offset.x < -50 || info.velocity.x < -350) go(1)
    else if (info.offset.x > 50 || info.velocity.x > 350) go(-1)
  }
  const fade = { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: reduced ? 0 : 0.55, ease: EASE } }

  return (
    <StickyShell cardRef={ref}>
      {/* LEFT — quote mare sus · (nume pe mobil) + departament + dots la bază */}
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.16}
        dragMomentum={false}
        onDragEnd={onDragEnd}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
        onKeyDown={(e) => { if (e.key === 'ArrowRight') go(1); else if (e.key === 'ArrowLeft') go(-1) }}
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label={`${label} — applications`}
        style={{ background: PANEL_BG, touchAction: 'pan-y' }}
        className="flex cursor-grab flex-col justify-between p-7 outline-none active:cursor-grabbing md:p-12 lg:p-14"
      >
        <blockquote className="flex flex-1 items-center">
          <motion.p key={idx} {...fade} className="max-w-[20ch] select-none font-sans text-[26px] font-medium leading-[1.2] tracking-[-0.01em] text-cream md:text-[34px] lg:text-[40px]">
            {descs[idx]}
          </motion.p>
        </blockquote>

        <div className="mt-8">
          {/* numele aplicației — vizibil pe mobil (pe desktop e uriaș în dreapta) */}
          <motion.p key={`m${idx}`} {...fade} className="font-sans text-[18px] font-medium text-cream md:hidden">{apps[idx]}</motion.p>
          <p className="mt-1 font-mono text-[12px] uppercase tracking-[0.16em] text-cream/45 md:mt-0">{label}</p>

          <div className="mt-6 flex items-center gap-2.5" role="tablist" aria-label={`${label} apps`}>
            {apps.map((a, k) => (
              <button key={a} type="button" role="tab" aria-selected={k === idx} aria-label={a} onClick={() => setIdx(k)} className="p-1">
                <span className={`block h-2.5 w-2.5 rounded-full bg-cream transition-opacity duration-300 ${k === idx ? 'opacity-100' : 'opacity-30 hover:opacity-60'}`} />
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* RIGHT — imaginea aplicației (dacă există) sau numele uriaș; crossfade + zoom per slide */}
      <Poster
        name={apps[idx]}
        nameColor={card.nameColor}
        visual={card.visual}
        slideKey={idx}
        reduced={reduced}
        imgSrc={HAS_IMG.has(`${card.slug}-${idx + 1}`) ? `/img/apps/${card.slug}-${idx + 1}.webp` : null}
      />
    </StickyShell>
  )
}

function BenefitsCard({ roi }) {
  const ref = useRef(null)
  return (
    <StickyShell cardRef={ref}>
      <div className="flex flex-col justify-between p-7 md:p-12 lg:p-14" style={{ background: PANEL_BG }}>
        <blockquote className="flex flex-1 items-center">
          <h3 className="font-sans text-[30px] font-semibold leading-[1.08] tracking-[-0.02em] text-cream md:text-[40px] lg:text-[46px]">{roi.title}</h3>
        </blockquote>
        <div className="mt-8">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.16em] text-cream/45">{roi.tag} ✦</p>
          <ul className="max-w-[48ch]">
            {roi.points.map((p) => (
              <li key={p} className="flex gap-3 border-b border-cream/10 py-3 text-[15px] leading-snug text-cream-soft md:text-[16px]">
                <span aria-hidden className="mt-[2px] select-none text-honey">→</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Poster name={roi.poster} nameColor="text-forest/80" visual="linear-gradient(150deg, #F0B453 0%, #E8A33D 45%, #C68A30 100%)" slideKey="roi" reduced={false} />
    </StickyShell>
  )
}

export default function Products() {
  const locale = useLocale()
  const h = HEADER[locale] || HEADER.en
  const roi = ROI[locale] || ROI.en

  return (
    <section id="products" data-theme="dark" className="relative px-5 pt-32 md:px-10 md:pt-40">
      <div className="mx-auto max-w-site">
        <header>
          <p className="font-mono text-[13px] uppercase tracking-[0.06em] text-cream">{h.eyebrow} ✦</p>
          <h2 className="display mt-5 max-w-[22ch] text-4xl leading-[1.0] md:text-6xl">
            <RevealLines onMount lines={[h.line1, <em key="em" className="display-italic">{h.line2}</em>]} />
          </h2>
          <p className="mt-6 max-w-[64ch] text-[16px] leading-relaxed text-cream-soft md:text-[18px]">{h.sub}</p>
        </header>
      </div>

      {/* STIVA — toate la același top → suprapunere perfectă. Gap mare = scroll lent. */}
      <div className="mx-auto mt-14 flex max-w-site flex-col gap-[24vh] pb-[26vh] md:mt-20 md:gap-[30vh]">
        {CARDS.map((card) => (
          <DeptCard
            key={card.slug}
            card={card}
            label={(LABELS[card.slug][locale] || LABELS[card.slug].en).toUpperCase()}
            descs={DESCS[card.slug][locale] || DESCS[card.slug].en}
          />
        ))}
        <BenefitsCard roi={roi} />
      </div>

      <div id="products-end" aria-hidden className="h-px w-full" />
    </section>
  )
}
